/**
 * Schema-management init flow: bootstrap, release-version fetch/change,
 * reset changes, and navigation-away handler.
 */

import { getStore } from "@store";
import schemaManagementAdapter from "@features/schema-management/adapters";
import { getArrItemByKey } from "@utils/search-helpers";
import { findMaxMinByKey } from "@utils/array-helpers";
import { isEmptyObject } from "@utils/object-helpers";
import {
  getCTIDOptions,
  getCommercialCTIDOptions,
  getConfigurationDetails,
} from "@interfaces/config";
import {
  showSpinner,
  stopSpinner,
  closeDock,
  clearChanges,
  clearLog,
  enableSave,
  setTabErrors,
  setToastMessage,
} from "@actions/global";
import {
  setReleaseVersion,
  setInitialReleaseVersion,
  setDefaultReleaseVersion,
  loadReleaseVersions,
  setSelectedPreviewTab,
  setDeletedClients,
} from "@features/schema-management/actions";
import { fetchSchemaManagement } from "./sm-fetch";

export const schemaMangementInit = async (store = getStore()) => {
  getCTIDOptions(store);
  getCommercialCTIDOptions(store);
  getConfigurationDetails(store);
  fetchAndLoadReleaseVersions(store);
};

export const fetchSchemaReleaseVersions = async (
  changeParans = {},
  ctid = ""
) =>
  await schemaManagementAdapter.fetchSMSchemaReleaseVersions(
    changeParans,
    ctid
  );

export const fetchConfigReleaseVersions = async (
  changeParans = {},
  ctid = "",
  store = getStore()
) => {
  const configReleaseVersions =
    await schemaManagementAdapter.fetchSMConfigReleaseVersions(
      changeParans,
      ctid
    );
  let currentReleaseVersion = "",
    isActivedReleaseEnabled = false;
  let updatedReleaseVersions = [];

  if (
    configReleaseVersions &&
    configReleaseVersions?.length &&
    Array.isArray(configReleaseVersions)
  ) {
    updatedReleaseVersions = configReleaseVersions?.filter(
      (release_version) => release_version?.is_enabled
    );
    const activeReleaseVersion = store.getState().schema?.activeReleaseVersion;
    isActivedReleaseEnabled = (
      activeReleaseVersion
        ? getArrItemByKey(
            "release_version",
            activeReleaseVersion,
            updatedReleaseVersions
          )
        : {}
    )?.is_enabled;
    const activeReleaseVersionDetails = findMaxMinByKey(
      updatedReleaseVersions,
      "release_version"
    );

    currentReleaseVersion =
      isActivedReleaseEnabled && activeReleaseVersion
        ? activeReleaseVersion
        : !isEmptyObject(activeReleaseVersionDetails)
          ? activeReleaseVersionDetails.release_version
          : "";
    {
    }
  }
  return {
    currentReleaseVersion,
    updatedReleaseVersions,
  };
};

export const fetchAndLoadReleaseVersions = async (store = getStore()) => {
  const { currentReleaseVersion, updatedReleaseVersions } =
    await fetchConfigReleaseVersions();

  if (updatedReleaseVersions && updatedReleaseVersions.length) {
    store.dispatch(setReleaseVersion(`${currentReleaseVersion}`));
    store.dispatch(setInitialReleaseVersion(`${currentReleaseVersion}`));
    store.dispatch(setDefaultReleaseVersion(`${currentReleaseVersion}`));
    store.dispatch(loadReleaseVersions(updatedReleaseVersions));
  }
  return { currentReleaseVersion, updatedReleaseVersions };
};

export const handleChangeReleaseVersion = async (
  release_version,
  isAthosConfig = false,
  store = getStore()
) => {
  store.dispatch(enableSave());
  store.dispatch(showSpinner());
  store.dispatch(setReleaseVersion(release_version));
  store.dispatch(closeDock());
  await fetchSchemaManagement(release_version, true);
  store.dispatch(setSelectedPreviewTab(release_version));
  store.dispatch(clearChanges());
  store.dispatch(clearLog());
  await fetchSchemaManagement(release_version, true, isAthosConfig);
  store.dispatch(enableSave());
  store.dispatch(stopSpinner());
};

export const resetChanges = (store = getStore()) => {
  store.dispatch(showSpinner());
  store.dispatch(clearChanges());
  store.dispatch(clearLog());
  store.dispatch(setTabErrors({}));
  store.dispatch(setDeletedClients({}));
  store.dispatch(closeDock());
  store.dispatch(enableSave());
  store.dispatch(stopSpinner());
};

export const handleLeavingSMFeature = (redirectTo, store = getStore()) => {
  store.dispatch(showSpinner());
  resetChanges();
  store.dispatch(
    setToastMessage(`Changes have been reset.`, () => {
      window.location.href = redirectTo;
    })
  );
  store.dispatch(stopSpinner());
};
